import * as React from "react";
import { Alert, Button, ButtonDropdown, SpaceBetween } from "@amzn/awsui-components-react";

/**
 * Supported target languages for on-demand AI page translation.
 * Kept in alphabetical order by English display name. English is the authoritative
 * source and is therefore not a selectable target (users "Revert to English").
 */
export const PAGE_TRANSLATION_LANGUAGES: { id: string; text: string }[] = [
    { id: "ar", text: "🇦🇪 Arabic" },
    { id: "fr", text: "🇫🇷 French" },
    { id: "de", text: "🇩🇪 German" },
    { id: "id", text: "🇮🇩 Indonesian" },
    { id: "ja", text: "🇯🇵 Japanese" },
    { id: "ko", text: "🇰🇷 Korean" },
    { id: "pt", text: "🇧🇷 Portuguese" },
    { id: "zh-Hans", text: "🇨🇳 Simplified Chinese" },
    { id: "es", text: "🇪🇸 Spanish" }
];

/**
 * Hook that encapsulates the on-demand page translation UI state.
 *
 * Selection is intentionally in-memory only: refreshing or navigating to a new
 * page resets to the English (authoritative) view and requires re-selecting a
 * language. No selection is persisted.
 *
 * Returns two pieces of JSX to be placed by the caller:
 *  - `trigger`: the "🌐 Translate" ButtonDropdown, meant for the top-right of the title line.
 *  - `banner`: the confirmation (warning) / translated (info) banner, meant above the page title.
 *
 * Pass the current page id so the state resets when the user navigates to a
 * different live page (PageLive does not remount on an in-app page-to-page
 * navigation — only its `id` prop changes), keeping the experience per-page.
 */
export const usePageTranslate = (pageId: string): {
    trigger: React.ReactNode;
    banner: React.ReactNode;
} => {
    // Language awaiting user confirmation (warning banner shown).
    const [pendingLang, setPendingLang] = React.useState<{ id: string; text: string } | null>(null);
    // Language currently applied after confirmation (info banner shown).
    const [activeLang, setActiveLang] = React.useState<{ id: string; text: string } | null>(null);

    // Reset to the English view when the page changes. PageLive stays mounted across
    // in-app navigation between live pages (only `pageId` changes), so without this the
    // prior page's banner/selection would carry over onto a page the user never translated.
    React.useEffect(() => {
        setPendingLang(null);
        setActiveLang(null);
    }, [pageId]);

    const onSelectLanguage = (id: string) => {
        const lang = PAGE_TRANSLATION_LANGUAGES.find(l => l.id === id);
        if (!lang) return;
        setPendingLang(lang);
    };

    const onContinue = () => {
        if (!pendingLang) return;
        // TODO: invoke the page translation backend for `pendingLang.id` and
        // render the returned translated content. No backend call is wired yet.
        setActiveLang(pendingLang);
        setPendingLang(null);
    };

    const onCancel = () => {
        setPendingLang(null);
    };

    const onRevertToEnglish = () => {
        setActiveLang(null);
        setPendingLang(null);
    };

    const trigger = (
        <ButtonDropdown
            items={PAGE_TRANSLATION_LANGUAGES}
            onItemClick={({ detail }) => onSelectLanguage(detail.id)}
        >
            🌐 Translate
        </ButtonDropdown>
    );

    let banner: React.ReactNode = null;
    if (pendingLang) {
        banner = (
            <Alert
                type="warning"
                action={
                    <SpaceBetween direction="horizontal" size="xs">
                        <Button variant="primary" onClick={onContinue}>Continue</Button>
                        <Button onClick={onCancel}>Cancel</Button>
                    </SpaceBetween>
                }
            >
                <span style={{ color: "#8d6605" }}>
                    You are about to view an AI-translated version of this page. The English version
                    is the authoritative source. Translated content may contain inaccuracies. Do you
                    wish to continue?
                </span>
            </Alert>
        );
    } else if (activeLang) {
        banner = (
            <Alert
                type="info"
                action={<Button onClick={onRevertToEnglish}>Revert to English</Button>}
            >
                <span style={{ color: "#0972d3" }}>
                    This page was automatically translated by AI. The English version is the
                    authoritative source. Please verify critical details independently.
                </span>
            </Alert>
        );
    }

    return { trigger, banner };
};
